import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CalendarClock, AlertTriangle, CheckCircle2, Sparkles, Building2 } from "lucide-react";

function toneFor(days, scaduto) {
  if (scaduto) return { cls: "text-red-600 bg-red-50", label: "Scaduto" };
  if (days <= 7) return { cls: "text-red-600 bg-red-50", label: `${days}g` };
  if (days <= 15) return { cls: "text-amber-600 bg-amber-50", label: `${days}g` };
  return { cls: "text-slate-600 bg-slate-100", label: `${days}g` };
}

export function NotificationBell() {
  const [scadenze, setScadenze] = useState([]);
  const [nuovi, setNuovi] = useState([]);
  const [count, setCount] = useState(0);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setScadenze(data.notifiche || []);
      setNuovi(data.nuovi || []);
      setCount(data.count || 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openNew = async (n) => {
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch {
      /* ignore */
    }
    navigate(`/app/aziende/${n.company_id}/bandi`);
    load();
  };

  const openDeadline = (n) => navigate(`/app/aziende/${n.company_id}/bandi`);

  const isEmpty = scadenze.length === 0 && nuovi.length === 0;

  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button
          className="relative rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          data-testid="notification-bell-button"
          aria-label="Notifiche"
        >
          <Bell className="h-5 w-5" />
          {count > 0 && (
            <span
              data-testid="notification-count"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
            >
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="border-b border-slate-100 px-4 py-3">
          <span className="font-display text-sm font-bold text-slate-900">Notifiche</span>
        </div>

        {isEmpty ? (
          <div className="flex flex-col items-center gap-2 px-4 py-8 text-center text-sm text-slate-500" data-testid="notification-empty">
            <CheckCircle2 className="h-6 w-6 text-emerald-500" />
            Tutto tranquillo: nessun nuovo bando o scadenza imminente.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {nuovi.length > 0 && (
              <div>
                <div className="flex items-center gap-2 bg-indigo-50/60 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-indigo-600">
                  <Sparkles className="h-3.5 w-3.5" /> Nuovi bandi trovati
                </div>
                <ul>
                  {nuovi.map((n) => (
                    <li key={n.id}>
                      <button
                        onClick={() => openNew(n)}
                        data-testid="notification-new-grant-item"
                        className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${
                          n.read ? "opacity-60" : ""
                        }`}
                      >
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-indigo-100 text-[11px] font-bold text-indigo-700">
                          {n.count}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-800">
                            {n.count} nuov{n.count === 1 ? "o bando" : "i bandi"}
                            {!n.read && <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-indigo-500 align-middle" />}
                          </span>
                          <span className="flex items-center gap-1 truncate text-xs text-slate-500">
                            <Building2 className="h-3 w-3" />{n.company_name}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {scadenze.length > 0 && (
              <div>
                <div className="flex items-center gap-2 bg-amber-50/60 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-amber-600">
                  <CalendarClock className="h-3.5 w-3.5" /> Scadenze bandi
                </div>
                <ul>
                  {scadenze.map((n) => {
                    const t = toneFor(n.giorni_rimanenti, n.scaduto);
                    return (
                      <li key={n.favorite_id}>
                        <button
                          onClick={() => openDeadline(n)}
                          data-testid="notification-item"
                          className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50"
                        >
                          <span className={`mt-0.5 flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold ${t.cls}`}>
                            {n.scaduto ? <AlertTriangle className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}
                            {t.label}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-slate-800">{n.titolo}</span>
                            <span className="block truncate text-xs text-slate-500">
                              {n.company_name} · scad. {n.scadenza}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
